/**
 * Theme, colour-vision mode and the toast stack.
 *
 * These are the only stores in the client that a *user gesture* writes to; every
 * other store is written by the rAF flush. They live here anyway, on the same
 * `ExternalStore`, for one reason: a React context holding the theme would
 * re-render the entire tree on a change, and the tree includes an orderbook
 * ladder that plan §2.3 forbids reconciling. A store that publishes a version
 * number re-renders only what subscribed.
 *
 * `notify()` is called directly here rather than by the flush, because a click
 * is not a frame of market data and there is nothing to coalesce it with.
 */

import type { ReactNode } from 'react'
import { ExternalStore } from './store'

export type ThemeChoice = 'dark' | 'light' | 'system'
export type ThemeResolved = 'dark' | 'light'

/**
 * `tokens.css` ships `[data-cvd="deuteranopia"]` and `[data-cvd="protanopia"]`,
 * which remap UP→blue and DOWN→amber across every `up-*`/`down-*` token
 * including the depth-bar and flash alphas (design-system README §2.5). The
 * remap is the *third* redundancy, not the first: the glyph, the explicit sign
 * and the fixed left/right position carry the meaning before colour does.
 */
export type CvdMode = 'none' | 'deuteranopia' | 'protanopia'

export interface UiState {
  theme: ThemeChoice
  /** What `data-theme` is actually set to once `system` is resolved. */
  resolvedTheme: ThemeResolved
  cvd: CvdMode
  /**
   * The pro ladder — one clickable 99-row book in place of the orderbook and
   * the order ticket.
   *
   * A layout preference rather than a capability: it changes which panels the
   * market page renders and nothing about what the account may do. It lives
   * beside the theme for the reason the file's header gives — a context holding
   * it would re-render the tree on a toggle, and the tree contains a ladder.
   *
   * Off by default, and honoured only from `lg` up: below 1120px there is no
   * ticket column for the wide ladder to span, so `<TradeRoute>` reads it as
   * `proMode && ticketHasItsOwnColumn` and the flag simply persists unused.
   */
  proMode: boolean
  /**
   * Click to trade on the standard orderbook — a click on a level places an
   * order at that price, sized from the ticket's Shares box.
   *
   * A per-device preference like `proMode`, off by default, and honoured only
   * where the ticket has its own column beside the book (`<BookPanel>`'s
   * `clickable`): below 1120px the ticket is a sheet, and a click would place a
   * size nobody can see. The box itself is rendered by the ticket.
   */
  clickToTrade: boolean
  /**
   * The market order's three one-tap buy presets, in dollars, in their stored
   * spelling (`25`, `2.50`) — `formatOneTapAmount`, `docs/07` §1c. Written
   * when an edit is saved, never per keystroke: `<TradeRoute>` subscribes to
   * the whole of this store.
   */
  oneTapAmounts: readonly [string, string, string]
}

export const THEME_STORAGE_KEY = 'ud.theme'
export const CVD_STORAGE_KEY = 'ud.cvd'
export const PRO_MODE_STORAGE_KEY = 'ud.pro'
export const CLICK_TO_TRADE_STORAGE_KEY = 'ud.book.click-to-trade'
export const ONE_TAP_AMOUNTS_STORAGE_KEY = 'ud.ticket.one-tap.amounts'

/** The buy presets until a user edits them: $5, $25 and $100. */
export const ONE_TAP_DEFAULT_AMOUNTS: readonly [string, string, string] = ['5', '25', '100']

export const uiStore = new ExternalStore<UiState>({
  theme: 'dark',
  resolvedTheme: 'dark',
  cvd: 'none',
  proMode: false,
  clickToTrade: false,
  oneTapAmounts: ONE_TAP_DEFAULT_AMOUNTS,
})

/* --- toasts ---------------------------------------------------------
 * Bottom-right stack, max 3 (components.md §3). Errors persist; everything else
 * auto-dismisses after 5s. The dismissal timer belongs to <ToastHost>, not to
 * this store — a store that owns a `setTimeout` is a store that leaks one.
 * ------------------------------------------------------------------- */

export type ToastTone = 'info' | 'success' | 'warning' | 'danger'

export interface Toast {
  id: string
  tone: ToastTone
  /**
   * `ReactNode`, not `string`, since Phase 7.
   *
   * The settlement receipt's toast carries `▲ UP`, and `▲`/`▼` render through
   * `<Glyph>` and never as a character in a string: they exist only in the mono
   * face and only because a 2.6 KB patch file puts them there (§F17). A toast
   * title typed as `string` cannot honour that rule, so it stops being one.
   */
  title: ReactNode
  body?: ReactNode
  /** Errors persist until dismissed (components.md §3). */
  persist: boolean
  /** Rendered in the toast's own row; used by fill toasts for side/size/price. */
  detail?: ReactNode
  /**
   * Auto-dismiss, overriding the 5-second default.
   *
   * One caller: the settlement receipt, which `components.md` §2.6 gives a
   * **6-second** toast. Not a knob — it is the one place the design names a
   * different number, and the number is there because the receipt is the answer
   * to "did I win?" and is worth one second more than a fill.
   */
  dismissMs?: number
  /**
   * The bracketed affordance `docs/07` §2 attaches to a dozen of its codes —
   * `[Sign in]`, `[Enable a new one]`, `[Cancel all]`, `[Contact support]`.
   *
   * Rule 2 of that document is "say what to do next, **in the same sentence
   * where possible**", and where it is not possible the remedy is a button. A
   * toast that names a remedy the user then has to go and find is a toast that
   * describes a problem, which rule 2 exists to prevent.
   */
  action?: { label: string; run: () => void }
}

export const MAX_TOASTS = 3

export const toastStore = new ExternalStore<{ toasts: Toast[] }>({ toasts: [] })

let toastSeq = 0

export function pushToast(toast: Omit<Toast, 'id' | 'persist'> & { persist?: boolean }): string {
  const id = `t${++toastSeq}`
  toastStore.mutate((s) => {
    s.toasts = [...s.toasts, { ...toast, id, persist: toast.persist ?? toast.tone === 'danger' }]
    // Oldest out first. A stack that grows past three covers the ticket.
    if (s.toasts.length > MAX_TOASTS) s.toasts = s.toasts.slice(s.toasts.length - MAX_TOASTS)
  })
  toastStore.notify()
  return id
}

export function dismissToast(id: string): void {
  toastStore.mutate((s) => {
    s.toasts = s.toasts.filter((t) => t.id !== id)
  })
  toastStore.notify()
}

/**
 * A request from somewhere else in the app to bring one of the market page's
 * bottom tabs forward.
 *
 * One caller: the "while you were away" summary's `[Settled]` link (§9.2.5 step
 * 4), which is raised by the runtime's reconciliation and has to reach a tab
 * selection that lives in `<TradeRoute>`'s own `useState`. A store rather than a
 * prop chain because the two ends are a non-React module and a leaf of the
 * route, and threading a callback between them would put the reconciliation on
 * the render path.
 *
 * The **nonce** is what makes it work twice: asking for the tab that is already
 * requested has to be observable, or a second reconnect a minute later is
 * silent.
 */
export const bottomTabRequest = new ExternalStore<{ tab: string | null; nonce: number }>({
  tab: null,
  nonce: 0,
})

export function requestBottomTab(tab: string): void {
  bottomTabRequest.set({ tab, nonce: bottomTabRequest.state.nonce + 1 })
  bottomTabRequest.notify()
}

/** Tests only. */
export function __resetToasts(): void {
  toastStore.set({ toasts: [] })
  toastStore.notify()
  toastSeq = 0
}
